const IDENTITY_FILE_COUNT = { CNI: 2, TITRE_DE_SEJOUR: 2, PASSEPORT: 1 };
const UNIVERSIGN_DOCUMENTS = ['CNI', 'PASSEPORT'];
const CNI_EXPIRY_TOLERANCE_YEARS = 5;
const MAX_IDENTITY_FILE_SIZE = 4 * 1024 * 1024;

export interface GedDocumentTag {
  label: string;
  level: 'success' | 'danger' | 'warning' | 'neutral';
}

export interface GedDocumentCheck {
  status: 'pending' | 'ok' | 'failed';
  pageCount?: number;
  byteLength?: number;
}

export function expectedFileCount(doc: any): number {
  return IDENTITY_FILE_COUNT[doc?.document] || 0;
}

export function isUniversignDocument(doc: any): boolean {
  return UNIVERSIGN_DOCUMENTS.includes(doc?.document);
}

export function fileCount(doc: any): number {
  return (doc?.files || []).length;
}

export function hasNoFile(doc: any): boolean {
  return !fileCount(doc);
}

export function toDate(value: any): Date {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

export function isExpired(doc: any): boolean {
  const expiry = toDate(doc?.dateExpired);
  if (!expiry) {
    return false;
  }

  if (doc?.document === 'CNI') {
    expiry.setFullYear(expiry.getFullYear() + CNI_EXPIRY_TOLERANCE_YEARS);
  }

  return expiry.getTime() < Date.now();
}

export function countPdfPages(bytes: ArrayBuffer): number {
  try {
    const text = new TextDecoder('latin1').decode(new Uint8Array(bytes));
    const matches = text.match(/\/Type\s*\/Page(?![s])/g);
    return matches && matches.length > 0 ? matches.length : 1;
  } catch (error) {
    console.error('Lecture PDF impossible pour compter les pages', error);
    return 1;
  }
}

export async function countPdfPagesInFile(file: File): Promise<number> {
  const isPdf =
    file?.type === 'application/pdf' ||
    file?.name?.toLowerCase().endsWith('.pdf');

  if (!isPdf) {
    return 1;
  }

  try {
    return countPdfPages(await file.arrayBuffer());
  } catch (error) {
    console.error('Lecture PDF impossible pour compter les pages', error);
    return 1;
  }
}

export function isUniversignCompliant(
  doc: any,
  check: GedDocumentCheck,
): boolean {
  if (!isUniversignDocument(doc) || check?.status !== 'ok') {
    return false;
  }

  const expected = expectedFileCount(doc);
  if (!expected || fileCount(doc) !== expected) {
    return false;
  }

  if (!toDate(doc?.dateExpired) || isExpired(doc)) {
    return false;
  }

  if (check.pageCount !== expected) {
    return false;
  }

  return check.byteLength <= MAX_IDENTITY_FILE_SIZE;
}

export function buildGedDocumentTags(
  doc: any,
  check: GedDocumentCheck,
): GedDocumentTag[] {
  const tags: GedDocumentTag[] = [];
  const expected = expectedFileCount(doc);
  const expired = isExpired(doc);
  const wrongFileCount = !!expected && fileCount(doc) !== expected;

  if (hasNoFile(doc)) {
    tags.push({ label: 'Aucun fichier', level: 'danger' });
  } else if (check?.status === 'failed') {
    tags.push({ label: 'Aperçu indisponible', level: 'danger' });
  }

  if (expired) {
    tags.push({ label: 'Expiré', level: 'danger' });
  }

  if (wrongFileCount) {
    tags.push({
      label: expected === 1 ? 'Doit tenir en 1 fichier' : 'Recto/verso attendu',
      level: 'warning',
    });
  }

  if (isUniversignDocument(doc) && !hasNoFile(doc)) {
    if (check?.status === 'pending') {
      tags.push({ label: 'Vérification…', level: 'neutral' });
    } else if (isUniversignCompliant(doc, check)) {
      tags.push({ label: 'Conforme Universign', level: 'success' });
    }
  }

  if (doc?.reviewed === false) {
    tags.push({ label: 'Non validé', level: 'neutral' });
  }

  return tags;
}
